import { useCallback, useEffect, useRef, useState } from "react";
import i18n from "../../../../utils/i18n";
import QuickResultProviderCard from "./QuickResultProviderCard.jsx";
import { getQuickResultProviderKey } from "./quickResultProviders";
import classes from "./quickResultProviderCarousel.module.scss";

/** Two full sequential passes (not per-card pairs) so getMarqueeSetWidthPx's loop math lines up. */
const buildMarqueeItems = (providers) => {
  const keyed = providers.map((provider, slotIndex) => ({
    provider,
    providerKey: getQuickResultProviderKey(provider, slotIndex),
  }));

  return ["a", "b"].flatMap((suffix) =>
    keyed.map(({ provider, providerKey }) => ({
      provider,
      cardKey: `${providerKey}-marquee-${suffix}`,
      providerKey,
    })),
  );
};

const DEFAULT_CARD_WIDTH_PX = 260;
const DEFAULT_CARD_GAP_PX = 16;
/** Higher = faster scroll. Duration = one-set width ÷ this value (seconds). */
const MARQUEE_SPEED_PX_PER_SEC = 32;

const readCardMetrics = (marqueeEl) => {
  const track = marqueeEl?.firstElementChild;
  const firstCard = track?.firstElementChild;

  if (!track || !firstCard) {
    return { cardWidth: DEFAULT_CARD_WIDTH_PX, cardGap: DEFAULT_CARD_GAP_PX };
  }

  const trackStyles = getComputedStyle(track);
  const cardGap =
    parseFloat(trackStyles.columnGap || trackStyles.gap) || DEFAULT_CARD_GAP_PX;
  const cardWidth = firstCard.getBoundingClientRect().width || DEFAULT_CARD_WIDTH_PX;

  return { cardWidth, cardGap };
};

const getMarqueeSetWidthPx = (providerCount, cardWidth, cardGap) =>
  providerCount * (cardWidth + cardGap) - cardGap;

const normalizeOffset = (offset, setWidth) => {
  if (setWidth <= 0) {
    return 0;
  }
  let next = offset % setWidth;
  if (next > 0) {
    next -= setWidth;
  }
  return next;
};

const QuickResultProviderCarousel = ({ providers, selectedIndustry }) => {
  const marqueeRef = useRef(null);
  const offsetRef = useRef(0);
  const dragRef = useRef({ active: false, startX: 0, startOffset: 0, pointerId: null });
  const [offsetPx, setOffsetPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [cardMetrics, setCardMetrics] = useState({
    cardWidth: DEFAULT_CARD_WIDTH_PX,
    cardGap: DEFAULT_CARD_GAP_PX,
  });

  const providerCount = providers?.length ?? 0;
  const { cardWidth, cardGap } = cardMetrics;
  const setWidthPx = getMarqueeSetWidthPx(providerCount, cardWidth, cardGap);

  useEffect(() => {
    const marqueeEl = marqueeRef.current;
    if (!marqueeEl || !providerCount) {
      return undefined;
    }

    const updateMetrics = () => {
      setCardMetrics(readCardMetrics(marqueeEl));
    };

    updateMetrics();

    const resizeObserver = new ResizeObserver(updateMetrics);
    resizeObserver.observe(marqueeEl);

    return () => resizeObserver.disconnect();
  }, [providerCount]);

  useEffect(() => {
    offsetRef.current = 0;
    setOffsetPx(0);
  }, [providerCount]);

  useEffect(() => {
    offsetRef.current = offsetPx;
  }, [offsetPx]);

  useEffect(() => {
    if (!providerCount || isDragging) {
      return undefined;
    }

    let rafId;
    let lastTime;

    const tick = (time) => {
      if (dragRef.current.active) {
        return;
      }
      if (!lastTime) {
        lastTime = time;
      }
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      const setWidth = getMarqueeSetWidthPx(providerCount, cardWidth, cardGap);
      const next = normalizeOffset(
        offsetRef.current - MARQUEE_SPEED_PX_PER_SEC * dt,
        setWidth,
      );
      offsetRef.current = next;
      setOffsetPx(next);

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [providerCount, isDragging, cardWidth, cardGap]);

  const handlePointerDown = useCallback((event) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    dragRef.current = {
      active: true,
      startX: event.clientX,
      startOffset: offsetRef.current,
      pointerId: event.pointerId,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) {
        return;
      }

      const delta = event.clientX - dragRef.current.startX;
      const next = normalizeOffset(dragRef.current.startOffset + delta, setWidthPx);
      offsetRef.current = next;
      setOffsetPx(next);
    },
    [setWidthPx],
  );

  const endDrag = useCallback((event) => {
    if (!dragRef.current.active || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    dragRef.current.active = false;
    setIsDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleDragStart = useCallback((event) => {
    event.preventDefault();
  }, []);

  if (!providerCount) {
    return null;
  }

  const marqueeItems = buildMarqueeItems(providers);

  return (
    <div
      ref={marqueeRef}
      className={`${classes.marquee} ${isDragging ? classes.marqueeDragging : ""}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDragStart={handleDragStart}
      role="region"
      aria-label={i18n.t("searchflow.foundaccountants")}
    >
      <div
        className={`${classes.track} ${isDragging ? classes.trackDragging : ""}`}
        style={{ transform: `translate3d(${offsetPx}px, 0, 0)` }}
      >
        {marqueeItems.map(({ provider, cardKey, providerKey }) => (
          <QuickResultProviderCard
            key={cardKey}
            provider={provider}
            providerKey={providerKey}
            selectedIndustry={selectedIndustry}
          />
        ))}
      </div>
    </div>
  );
};

export default QuickResultProviderCarousel;
